import { useEffect, useState } from 'react';

import { add, isAfter, isBefore, startOfDay } from 'date-fns';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import { Trans, useTransl } from 'shared/lib/i18n';

import GenericFormContainer from '../Forms/GenericFormContainer';
import SelectPersonContainer from '../Person/SelectPersonContainer';

import {
  formatSelect,
  formatSelectWithFixedValues,
  lengthValidation120,
  lengthValidation250,
  maxFileSize,
  required,
  validateDate,
  validateEmail,
  validateFutureDate,
  validatePhoneNumber,
} from '../Forms/utils';
import {
  canEditVisitor,
  getDefaultBuildingDoorsIds,
  getElectronicKeyPeriodOptions,
  getEndDayTime,
  getPersonOptions,
  getStartDayTime,
  getVisitorDoors,
  isSameTime,
} from './utils';

import { ButtonLink } from '../Button';
import {
  CHECKBOX,
  EXPANDABLE_IMAGE_COMPONENT,
  IMAGE_UPLOAD,
  MESSAGE,
  PHONE_NUMBER,
  REACT_SELECT_INPUT,
  STYLED_TEXT,
  TIME_PICKER,
  VISITOR_DATE_PICKER,
} from '../Forms/fieldTypes';
import AllowMultipleDevices from '../Person/AllowMultipleDevices';

const VisitorFormComponent = ({
  initialValues = {},
  onSubmit,
  visitor,
  filteredBuildings = {},
  filteredUnits = {},
  isWarningShown,
  closeWarning,
  visitorId,
  completeVisitor,
  shouldBePermanent,
  selectedBuilding,
  buildingDoors,
  visitorPhotoRequired,
  updateFormValue,
  form,
  formValues,
  person,
  refetchPerson,
}) => {
  const { t } = useTransl();

  const { email, phoneNumber, avatar, buildingOptions, authorizerOptions } =
    initialValues;

  const values = formValues || {};

  const isUpdate = Boolean(visitorId);
  const isEditable = visitor?.person?.editable;

  const selectedUnitId = values?.unit;
  const isSendKey = values?.sendElectronicKey;
  const visitorEmail = values?.email;
  const visitorPhoneNumber = values?.phoneNumber;
  const validFrom = values?.validFrom;
  const hasContactInfo = !!visitorEmail || !!visitorPhoneNumber;

  const selectedUnit = filteredUnits?.[selectedUnitId];
  const residentsCount = !isUpdate && selectedUnit?.residentsCount;
  const personOptions = getPersonOptions(selectedUnit);
  const selectedBuildingId = selectedBuilding?.id;

  const isUploadDisabled = values?.images?.length >= 1;
  const isDeletePhoto = values?.photo_deleted;
  const isTextUnitValue = typeof initialValues.unit === 'string';

  const doorsOptions = getVisitorDoors(buildingDoors, selectedUnitId);

  const selectedDoorsIDs = values?.doors;

  const electronicKeyPeriodOptions =
    getElectronicKeyPeriodOptions(visitorPhoneNumber);

  const canBeUpdated = canEditVisitor(visitor);

  const unitsOptions = formatSelect(
    Object.values(filteredUnits)?.filter(({ isSuite }) => !isSuite),
  );

  const [isClearable, setIsClearable] = useState(true);

  useEffect(() => {
    const selectedDoors =
      doorsOptions &&
      selectedDoorsIDs &&
      doorsOptions.filter(({ id }) => selectedDoorsIDs.includes(id));
    const clearable = selectedDoors?.some((v) => !v.defaultInBuilding);
    setIsClearable(!!clearable);
  }, [selectedDoorsIDs]);

  useEffect(() => {
    isSendKey && !hasContactInfo && updateFormValue('sendElectronicKey', false);
  }, [hasContactInfo]);

  useEffect(() => {
    if (isSendKey) {
      updateFormValue('doors', getDefaultBuildingDoorsIds(doorsOptions));
      updateFormValue('period', 'unlimited');
    } else {
      values?.period && updateFormValue('period', null);
      values?.doors && updateFormValue('doors', null);
    }
  }, [isSendKey]);

  useEffect(() => {
    if (isSendKey) {
      updateFormValue('doors', getDefaultBuildingDoorsIds(doorsOptions));
    }
  }, [buildingOptions, selectedBuilding]);

  useEffect(() => {
    if (!visitorPhoneNumber && values?.period === '15min') {
      updateFormValue('period', null);
    }
  }, [visitorPhoneNumber]);

  const isAllDayAllowed = isSameTime(
    values?.allowedTimeStart,
    values?.allowedTimeEnd,
  );

  const setAllDayAllowed = () => {
    updateFormValue('allowedTimeStart', getStartDayTime());
    updateFormValue('allowedTimeEnd', getEndDayTime());
  };

  const formData = [
    {
      label: t('title.userAvatar'),
      keyName: 'avatar',
      fieldType: EXPANDABLE_IMAGE_COMPONENT,
      src: avatar,
      alt: t('title.userAvatar'),
      height: '15rem',
      validate: [],
      isHidden: !isUpdate || !avatar,
    },
    {
      label: t('title.firstName'),
      keyName: 'firstName',
      fieldType: STYLED_TEXT,
      validate: [required, lengthValidation120],
      disabled: isUpdate && !isEditable,
    },
    {
      label: t('title.lastName'),
      keyName: 'lastName',
      fieldType: STYLED_TEXT,
      validate: [required, lengthValidation120],
      disabled: isUpdate && !isEditable,
    },
    {
      keyName: 'building',
      label: t('title.building'),
      fieldType: REACT_SELECT_INPUT,
      options:
        buildingOptions || formatSelect(Object.values(filteredBuildings)),
      validate: [required],
      disabled: isUpdate || !!buildingOptions,
    },
    {
      label: t('title.onBehalfOfUnit'),
      keyName: 'unit',
      fieldType: isTextUnitValue ? STYLED_TEXT : REACT_SELECT_INPUT,
      options: unitsOptions,
      validate: [],
      isHidden: !selectedBuildingId,
      disabled: isUpdate,
      isClearable: true,
    },
    {
      label: t('title.email'),
      keyName: 'email',
      fieldType: STYLED_TEXT,
      validate: email ? [required, validateEmail] : [validateEmail],
      disabled: isUpdate && !isEditable,
    },
    {
      label: t('title.phoneNumber'),
      keyName: 'phoneNumber',
      fieldType: PHONE_NUMBER,
      validate: phoneNumber
        ? [required, validatePhoneNumber]
        : [validatePhoneNumber],
      disabled: isUpdate && !isEditable,
      isHidden: !selectedBuilding,
    },
    {
      label: t('title.contactPerson'),
      keyName: 'authorizer',
      fieldType: REACT_SELECT_INPUT,
      options: authorizerOptions || personOptions,
      validate: residentsCount > 0 ? [required] : [],
      isHidden: !selectedUnitId || isUpdate,
      disabled: isUpdate || !!authorizerOptions,
      isClearable: true,
    },
    {
      label: t('title.contactPerson'),
      keyName: 'contactPerson',
      fieldType: STYLED_TEXT,
      validate: [],
      isHidden: !selectedBuildingId || !isUpdate,
      disabled: isUpdate,
    },
    {
      label: t('title.images'),
      keyName: 'images',
      fieldType: IMAGE_UPLOAD,
      validate:
        visitorPhotoRequired && !avatar
          ? [required, maxFileSize]
          : [maxFileSize],
      disabled: isUploadDisabled || isDeletePhoto || (isUpdate && !isEditable),
    },
    {
      label: t('title.deleteVisitorPhoto'),
      keyName: 'photo_deleted',
      fieldType: CHECKBOX,
      validate: [],
      isHidden: visitorPhotoRequired || !isUpdate || !avatar || !isEditable,
      disabled: isUploadDisabled,
    },
    {
      label: t('title.validFrom'),
      keyName: 'validFrom',
      fieldType: VISITOR_DATE_PICKER,
      validate: [validateDate],
      isHidden: !selectedBuildingId,
      shouldDisableDate: (day) => {
        const isAfterSevenDays = isAfter(
          day,
          add(startOfDay(new Date()), { days: 31 }),
        );
        return isAfterSevenDays;
      },
      isValidFrom: true,
      disablePast: true,
    },
    {
      label: `${t('title.validUntil')} ${t('title.permanentVisitor')}`,
      keyName: 'validUntil',
      fieldType: VISITOR_DATE_PICKER,
      validate: [validateDate, validateFutureDate],
      shouldBePermanent,
      isHidden: !selectedBuildingId,
      shouldDisableDate: (day) => {
        if (!validFrom) return false;
        const isBeforeValidFrom = isBefore(day, new Date(validFrom));
        return isBeforeValidFrom;
      },
      disablePast: true,
    },
    {
      keyName: 'message',
      fieldType: MESSAGE,
      value: isAllDayAllowed ? (
        t('title.allowedHours')
      ) : (
        <Trans i18nKey="visitor.allowedHours">
          <Box component="span" display="flex">
            Allowed Hours&nbsp;
            <ButtonLink onClick={setAllDayAllowed} type="button">
              (set all day)
            </ButtonLink>
          </Box>
        </Trans>
      ),
      isHidden: !selectedBuildingId,
    },
    {
      keyName: 'allowedTimeStart',
      label: t('time.from'),
      fieldType: TIME_PICKER,
      getDisabledHours: () => [],
      validate: [],
      isHidden: !selectedBuildingId,
      secondKeyName: 'allowedTimeEnd',
      secondLabel: t('time.to'),
    },
    {
      label: t('title.comments'),
      keyName: 'comments',
      fieldType: STYLED_TEXT,
      validate: [lengthValidation250],
    },
    {
      label: hasContactInfo
        ? t('visitor.sendDigitalKey')
        : t('visitor.sendDigitalKeyNoContact'),
      keyName: 'sendElectronicKey',
      fieldType: CHECKBOX,
      validate: [],
      isHidden: isUpdate || !selectedBuildingId,
      disabled: !hasContactInfo,
    },
    {
      label: t('title.period'),
      keyName: 'period',
      fieldType: REACT_SELECT_INPUT,
      options: electronicKeyPeriodOptions,
      validate: isSendKey ? [required] : [],
      isHidden: isUpdate || !isSendKey || !hasContactInfo,
    },
    {
      label: t('title.doors'),
      keyName: 'doors',
      fieldType: REACT_SELECT_INPUT,
      multi: true,
      options: formatSelectWithFixedValues(
        doorsOptions,
        'name',
        'id',
        'defaultInBuilding',
      ),
      validate: isSendKey ? [required] : [],
      isHidden: isUpdate || !isSendKey || !hasContactInfo,
      isClearable,
    },
  ];

  return (
    <Box>
      <GenericFormContainer
        form={form}
        formData={formData}
        onSubmit={onSubmit}
        initialValues={initialValues}
        canBeUpdated={canBeUpdated}
        isUpdate={!!isUpdate}
        title={isUpdate ? t('title.updateVisitor') : t('title.addVisitor')}
        isButtonDisabled={isWarningShown}
        enableReinitialize
      />
      {isWarningShown ? (
        <Box mt="3rem">
          <SelectPersonContainer
            onSelect={completeVisitor}
            onCancel={closeWarning}
            formValues={formValues}
          />
        </Box>
      ) : null}
      <AllowMultipleDevices person={person} refetchPerson={refetchPerson} />
    </Box>
  );
};

VisitorFormComponent.defaultProps = {
  formValues: {},
  buildingDoors: [],
  visitorId: null,
  selectedBuilding: {},
  visitor: null,
};

VisitorFormComponent.propTypes = {
  initialValues: PropTypes.instanceOf(Object).isRequired,
  form: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  filteredBuildings: PropTypes.instanceOf(Object).isRequired,
  filteredUnits: PropTypes.instanceOf(Object).isRequired,
  formValues: PropTypes.instanceOf(Object),
  isWarningShown: PropTypes.bool.isRequired,
  shouldBePermanent: PropTypes.func.isRequired,
  visitorPhotoRequired: PropTypes.bool.isRequired,
  updateFormValue: PropTypes.func.isRequired,
  buildingDoors: PropTypes.instanceOf(Array),
  completeVisitor: PropTypes.func.isRequired,
  closeWarning: PropTypes.func.isRequired,
  refetchPerson: PropTypes.func.isRequired,
  visitor: PropTypes.instanceOf(Object),
  visitorId: PropTypes.string,
  selectedBuilding: PropTypes.instanceOf(Object),
  person: PropTypes.instanceOf(Object),
};

export default VisitorFormComponent;
